import React from 'react'
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip
} from '@material-ui/core'
import VisibilityIcon from '@material-ui/icons/Visibility'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import CallSplitIcon from '@material-ui/icons/CallSplit'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'

const STANDARD_UNITS = ['EA', 'SF', 'LF', 'CY', 'TON', 'GAL', 'LS', 'ALLOW']

const confidenceChip = (confidence) => {
  switch ((confidence || '').toLowerCase()) {
    case 'high':
      return { label: 'High confidence', style: { backgroundColor: '#c8e6c9', color: '#2e7d32' } }
    case 'medium':
      return { label: 'Medium confidence', style: { backgroundColor: '#fff9c4', color: '#f57f17' } }
    case 'low':
      return { label: 'Low confidence', style: { backgroundColor: '#ffcdd2', color: '#c62828' } }
    default:
      return { label: confidence || 'Unknown', style: { backgroundColor: '#eeeeee', color: '#616161' } }
  }
}

const normalizeUnit = (value) => {
  const upper = String(value || '').trim().toUpperCase()
  if (!upper) return ''
  if (upper === 'S.F.' || upper === 'SF') return 'SF'
  if (upper === 'L.F.' || upper === 'LF') return 'LF'
  if (upper === 'GAL.' || upper === 'GALLON' || upper === 'GAL') return 'GAL'
  return upper
}

const buildRowDescription = (row) => {
  if (row.description) return row.description
  if (row.item) return row.item

  const commonName = String(row.commonName || '').trim()
  const botanicalName = String(row.botanicalName || '').trim()

  let base = ''
  if (commonName && botanicalName) base = `${commonName} (${botanicalName})`
  else base = commonName || botanicalName

  if (!base) return ''
  return base
}

const buildRowSize = (row) => String(row.size || '').trim()

const buildRowManufacturer = (row) => String(row.manufacturer || row.make || '').trim()

const buildRowModel = (row) => String(row.model || row.modelNumber || row.manufacturerSku || '').trim()

const buildRowQuantity = (row) => {
  if (row.quantity != null) return row.quantity
  if (row.qty != null) return row.qty
  return null
}

const buildRowUnit = (row) => {
  if (row.unit) return row.unit

  if (row.size && buildRowQuantity(row) != null) return 'EA'

  const text = `${row.sourceWords || ''} ${row.comments || ''}`
  const match = text.match(/\b(S\.?F\.?|L\.?F\.?|SF|LF|TON|CY|EA|GAL)\b/i)
  return match ? normalizeUnit(match[1]) : ''
}

const buildRowSection = (row) => {
  if (row.section) return row.section
  if (row.category) return `Planting - ${row.category}`
  return ''
}

const buildRowNotes = (row) => {
  const parts = [
    row.comments,
    row.remarks,
    row.notes
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  const deduped = []
  for (const part of parts) {
    if (!deduped.includes(part)) deduped.push(part)
  }
  return deduped.join(' | ')
}

const buildReviewerNotes = (rowEdits, rowIdx) => {
  if (!rowEdits || !rowEdits[rowIdx]) return ''
  return String(rowEdits[rowIdx].reviewerNotes || '').trim()
}

const buildRowSecondaryQuantities = (row) => {
  const primaryQty = buildRowQuantity(row)
  const primaryUnit = normalizeUnit(row.unit || (row.size && primaryQty != null ? 'EA' : ''))
  const text = `${row.comments || ''} ${row.sourceWords || ''}`
  const matches = []
  const seen = new Set()
  const re = /(\d[\d,]*(?:\.\d+)?)\s*(SF|LF|CY|TON|EA|GAL)\b/ig
  let match
  while ((match = re.exec(text)) !== null) {
    const qty = Number(String(match[1]).replace(/,/g, ''))
    const unit = normalizeUnit(match[2])
    if (!Number.isFinite(qty) || !unit) continue
    if (primaryQty != null && Number(primaryQty) === qty && (!primaryUnit || primaryUnit === unit)) continue
    const key = `${qty}:${unit}`
    if (seen.has(key)) continue
    seen.add(key)
    matches.push({ qty, unit })
  }
  return matches
}

const isImportableRow = (row) => {
  const description = buildRowDescription(row)
  const quantity = Number(buildRowQuantity(row)) || 0
  const unit = String(buildRowUnit(row) || '').trim()
  const isAllowance = /^(ls|allow)$/i.test(unit)
  return Boolean(description) && Boolean(unit) && (quantity > 0 || isAllowance)
}

const ImportTableCard = ({
  sheetIndex,
  tableIndex,
  table,
  buildings,
  state,
  onChange,
  // New props for visual review
  reviewSummary,
  hasVisualReview,
  onToggleVisualReview,
  rowEdits,
  onRowEdit,
  selectedRowIndex,
  onSelectRow,
  sectionSuggestions = [],
  fillAvailableHeight = false,
  addedRows = [],
  onAddRow,
  onComposeRow,
  onEditAddedRow,
  onRemoveAddedRow,
  importedRows
}) => {
  const rows = Array.isArray(table.rows) ? table.rows : []
  const skipSet = state.skipSet || new Set()
  const chip = confidenceChip(table.confidence)

  const [editingCell, setEditingCell] = React.useState(null)
  const sectionListId = `section-suggestions-${sheetIndex}-${tableIndex}`

  const getEditedValue = (rowIdx, field, original) => {
    if (rowEdits && rowEdits[rowIdx] && rowEdits[rowIdx][field] !== undefined) {
      return rowEdits[rowIdx][field]
    }
    return original
  }

  const isEdited = (rowIdx, field) => {
    return rowEdits && rowEdits[rowIdx] && rowEdits[rowIdx][field] !== undefined
  }

  const handleCellClick = (rowIdx, field) => {
    if (!onRowEdit) return
    setEditingCell({ row: rowIdx, field })
  }

  const handleCellBlur = (rowIdx, field, value) => {
    setEditingCell(null)
    if (!onRowEdit) return
    const row = rows[rowIdx] || {}
    const originals = {
      description: buildRowDescription(row),
      manufacturer: buildRowManufacturer(row),
      model: buildRowModel(row),
      unit: buildRowUnit(row),
      quantity: buildRowQuantity(row),
      reviewerNotes: '',
      section: buildRowSection(row)
    }
    const original = originals[field]
    if (String(value ?? '') === String(original ?? '')) return
    onRowEdit(rowIdx, field, value)
  }

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    } else if (e.key === 'Escape') {
      setEditingCell(null)
    }
  }

  // Auto-skip duplicate rows on first render
  React.useEffect(() => {
    const dupeIndexes = rows
      .map((row, idx) => (row.isDuplicate ? idx : null))
      .filter(idx => idx !== null)
    if (dupeIndexes.length > 0 && skipSet.size === 0) {
      onChange(sheetIndex, tableIndex, {
        ...state,
        skipSet: new Set(dupeIndexes)
      })
    }
  }, []) // eslint-disable-line

  const selectedCount = rows.filter((_, idx) => !skipSet.has(idx)).length + addedRows.length
  const importedCount = rows.filter((row, idx) => !skipSet.has(idx) && isImportableRow(row)).length +
    addedRows.filter(r => r.description && r.unit && (Number(r.quantity) > 0 || /^(ls|allow)$/i.test(r.unit))).length

  const toggleRow = (idx) => {
    const next = new Set(skipSet)
    if (next.has(idx)) next.delete(idx)
    else next.add(idx)
    onChange(sheetIndex, tableIndex, { ...state, skipSet: next })
  }

  const toggleAll = () => {
    if (skipSet.size === 0) {
      onChange(sheetIndex, tableIndex, {
        ...state,
        skipSet: new Set(rows.map((_, i) => i))
      })
    } else {
      onChange(sheetIndex, tableIndex, { ...state, skipSet: new Set() })
    }
  }

  const allChecked = skipSet.size === 0
  const someChecked = skipSet.size > 0 && skipSet.size < rows.length
  const showSizeColumn = rows.some((row) => Boolean(buildRowSize(row)))
  const showManufacturerColumn = rows.some((row) => Boolean(buildRowManufacturer(row))) || addedRows.some((row) => Boolean(row.manufacturer))
  const showModelColumn = rows.some((row) => Boolean(buildRowModel(row))) || addedRows.some((row) => Boolean(row.model))
  const showNotesColumn = rows.some((row) => Boolean(buildRowNotes(row)))

  const editedCellStyle = (rowIdx, field) => isEdited(rowIdx, field)
    ? { border: '1.5px solid #ff9800', borderRadius: 3, padding: '1px 4px', background: '#fff' }
    : {}

  return (
    <div style={fillAvailableHeight ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' } : {}}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        flexWrap: 'wrap'
      }}>
        <Chip
          size="small"
          label={chip.label}
          style={{ ...chip.style, height: 22 }}
        />
        {reviewSummary && reviewSummary.blockerCounts && Object.entries(reviewSummary.blockerCounts).map(([key, count]) => (
          count > 0 && (
            <Chip
              key={key}
              size="small"
              label={`${count} ${key.replace(/_/g, ' ')}`}
              style={{ backgroundColor: '#fff3e0', color: '#e65100', height: 20, fontSize: 10 }}
            />
          )
        ))}
        {reviewSummary && reviewSummary.secondaryQuantityRowCount > 0 && (
          <Chip
            size="small"
            label={`${reviewSummary.secondaryQuantityRowCount} secondary qty`}
            style={{ backgroundColor: '#fff8e1', color: '#f57f17', height: 20, fontSize: 10 }}
          />
        )}
        <Typography variant="caption" color="textSecondary">
          {selectedCount} selected, {importedCount} importable
        </Typography>
        {onAddRow && (
          <Tooltip title="Add a row manually">
            <IconButton size="small" onClick={onAddRow} style={{ marginLeft: hasVisualReview ? undefined : 'auto' }}>
              <AddIcon fontSize="small" style={{ color: '#2e7d32' }} />
            </IconButton>
          </Tooltip>
        )}
        {onComposeRow && (
          <Tooltip title="Compose a clean row from source text">
            <IconButton size="small" onClick={onComposeRow}>
              <CallSplitIcon fontSize="small" style={{ color: '#6d4c41' }} />
            </IconButton>
          </Tooltip>
        )}
        {hasVisualReview && onToggleVisualReview && (
          <Tooltip title="Toggle source preview">
            <IconButton size="small" onClick={onToggleVisualReview} style={{ marginLeft: onAddRow ? undefined : 'auto' }}>
              <VisibilityIcon fontSize="small" style={{ color: '#1565c0' }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {buildings.length > 0 ? (
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Building</InputLabel>
            <Select
              value={state.building || ''}
              onChange={(e) => onChange(sheetIndex, tableIndex, { ...state, building: e.target.value })}
              label="Building"
            >
              {buildings.map(b => {
                const name = typeof b === 'string' ? b : (b.name || '')
                return (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                )
              })}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Building"
            variant="outlined"
            size="small"
            value={state.building || ''}
            onChange={(e) => onChange(sheetIndex, tableIndex, { ...state, building: e.target.value })}
            style={{ minWidth: 200 }}
            placeholder="e.g. Main"
          />
        )}
        <TextField
          label="Default section (optional)"
          variant="outlined"
          size="small"
          value={state.section || ''}
          onChange={(e) => onChange(sheetIndex, tableIndex, { ...state, section: e.target.value })}
          style={{ minWidth: 220 }}
          helperText="Used when a row has no section"
          inputProps={{ list: sectionListId }}
        />
        {sectionSuggestions.length > 0 && (
          <datalist id={sectionListId}>
            {sectionSuggestions.map((section) => (
              <option key={section} value={section} />
            ))}
          </datalist>
        )}
      </div>

      <div style={{ maxHeight: fillAvailableHeight ? undefined : 360, flex: fillAvailableHeight ? 1 : undefined, minHeight: fillAvailableHeight ? 0 : undefined, overflow: 'auto', border: '1px solid #eeeeee', borderRadius: 4 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" style={{ backgroundColor: '#fafafa' }}>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={toggleAll}
                  size="small"
                />
              </TableCell>
              <TableCell style={{ backgroundColor: '#fafafa', width: 40 }}>#</TableCell>
              <TableCell style={{ backgroundColor: '#fafafa' }}>Description</TableCell>
              {showSizeColumn && <TableCell style={{ backgroundColor: '#fafafa' }}>Size</TableCell>}
              {showManufacturerColumn && <TableCell style={{ backgroundColor: '#fafafa' }}>Manufacturer</TableCell>}
              {showModelColumn && <TableCell style={{ backgroundColor: '#fafafa' }}>Model</TableCell>}
              <TableCell style={{ backgroundColor: '#fafafa' }}>Unit</TableCell>
              <TableCell align="right" style={{ backgroundColor: '#fafafa' }}>Qty</TableCell>
              {showNotesColumn && <TableCell style={{ backgroundColor: '#fafafa' }}>Notes</TableCell>}
              <TableCell style={{ backgroundColor: '#fafafa', minWidth: 220 }}>Reviewer Notes</TableCell>
              <TableCell style={{ backgroundColor: '#fafafa' }}>Section</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7 + (showSizeColumn ? 1 : 0) + (showManufacturerColumn ? 1 : 0) + (showModelColumn ? 1 : 0) + (showNotesColumn ? 1 : 0)} align="center">
                  <Typography variant="caption" color="textSecondary">
                    No rows in this table.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, i) => {
              const skipped = skipSet.has(i)
              const isImported = importedRows && importedRows.has(i)
              const isDupe = row.isDuplicate
              const description = getEditedValue(i, 'description', buildRowDescription(row))
              const size = buildRowSize(row)
              const manufacturer = getEditedValue(i, 'manufacturer', buildRowManufacturer(row))
              const model = getEditedValue(i, 'model', buildRowModel(row))
              const quantity = getEditedValue(i, 'quantity', buildRowQuantity(row))
              const unit = getEditedValue(i, 'unit', buildRowUnit(row))
              const notes = buildRowNotes(row)
              const reviewerNotes = getEditedValue(i, 'reviewerNotes', buildReviewerNotes(rowEdits, i))
              const section = getEditedValue(i, 'section', buildRowSection(row))
              const missingUnit = !String(unit || '').trim()
              const missingQty = !/^(ls|allow)$/i.test(String(unit || '').trim()) && !(Number(quantity) > 0)
              const secondaryQuantities = buildRowSecondaryQuantities(row)
              const hasSecondaryQty = secondaryQuantities.length > 0
              const isEditingDesc = editingCell && editingCell.row === i && editingCell.field === 'description'
              const isEditingManufacturer = editingCell && editingCell.row === i && editingCell.field === 'manufacturer'
              const isEditingModel = editingCell && editingCell.row === i && editingCell.field === 'model'
              const isEditingUnit = editingCell && editingCell.row === i && editingCell.field === 'unit'
              const isEditingQty = editingCell && editingCell.row === i && editingCell.field === 'quantity'
              const isEditingReviewerNotes = editingCell && editingCell.row === i && editingCell.field === 'reviewerNotes'
              const isEditingSection = editingCell && editingCell.row === i && editingCell.field === 'section'
              const isSelectedRow = selectedRowIndex === i

              return (
                <TableRow
                  key={i}
                  hover
                  onClick={() => onSelectRow && onSelectRow(i)}
                  style={{
                    opacity: skipped ? 0.45 : isImported ? 0.6 : 1,
                    textDecoration: skipped ? 'line-through' : 'none',
                    backgroundColor: isImported && !isSelectedRow ? '#e8f5e9'
                      : isSelectedRow ? '#e3f2fd'
                      : isDupe && !skipped ? '#fff3e0'
                      : ((missingUnit || missingQty) && !skipped) ? '#fff3e0'
                      : (hasSecondaryQty && !skipped) ? '#fffde7'
                      : undefined,
                    outline: isSelectedRow ? '2px solid #1565c0' : 'none',
                    outlineOffset: -2
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={!skipped}
                      onChange={() => toggleRow(i)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {row.itemNo != null ? row.itemNo : i + 1}
                      {isImported && <CheckCircleIcon style={{ fontSize: 13, color: '#2e7d32' }} />}
                    </span>
                  </TableCell>
                  <TableCell
                    style={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word', cursor: onRowEdit ? 'pointer' : 'default' }}
                    onClick={() => !isEditingDesc && handleCellClick(i, 'description')}
                  >
                    {isEditingDesc ? (
                      <textarea
                        autoFocus
                        defaultValue={description}
                        onBlur={(e) => handleCellBlur(i, 'description', e.target.value)}
                        onKeyDown={handleCellKeyDown}
                        rows={3}
                        style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #1976d2', borderRadius: 3, padding: '4px 6px', resize: 'vertical' }}
                      />
                    ) : (
                      <span style={editedCellStyle(i, 'description')}>
                        {description}
                        {isEdited(i, 'description') && <span style={{ fontSize: 9, color: '#ff9800', marginLeft: 4 }}>edited</span>}
                      </span>
                    )}
                    {isDupe && (
                      <Chip size="small" label="Duplicate"
                        style={{ marginLeft: 8, height: 18, fontSize: 10, backgroundColor: '#ff9800', color: '#fff' }} />
                    )}
                    {missingUnit && !skipped && (
                      <Chip size="small" label="No unit"
                        style={{ marginLeft: 8, height: 18, fontSize: 10, backgroundColor: '#ff9800', color: '#fff' }} />
                    )}
                    {missingQty && !skipped && (
                      <Chip size="small" label="No qty"
                        style={{ marginLeft: 8, height: 18, fontSize: 10, backgroundColor: '#ff9800', color: '#fff' }} />
                    )}
                    {hasSecondaryQty && !skipped && (
                      <Chip size="small" label="Qty in notes"
                        style={{ marginLeft: 8, height: 18, fontSize: 10, backgroundColor: '#fff176', color: '#795548' }} />
                    )}
                  </TableCell>
                  {showSizeColumn && <TableCell>{size || ''}</TableCell>}
                  {showManufacturerColumn && (
                    <TableCell
                      style={{ maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word', cursor: onRowEdit ? 'pointer' : 'default' }}
                      onClick={() => !isEditingManufacturer && handleCellClick(i, 'manufacturer')}
                    >
                      {isEditingManufacturer ? (
                        <input
                          autoFocus
                          defaultValue={manufacturer}
                          onBlur={(e) => handleCellBlur(i, 'manufacturer', e.target.value)}
                          onKeyDown={handleCellKeyDown}
                          style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #1976d2', borderRadius: 3, padding: '2px 4px' }}
                        />
                      ) : (
                        <span style={editedCellStyle(i, 'manufacturer')}>
                          {manufacturer || ''}
                          {isEdited(i, 'manufacturer') && <span style={{ fontSize: 9, color: '#ff9800', marginLeft: 4 }}>edited</span>}
                        </span>
                      )}
                    </TableCell>
                  )}
                  {showModelColumn && (
                    <TableCell
                      style={{ maxWidth: 170, whiteSpace: 'normal', wordBreak: 'break-word', cursor: onRowEdit ? 'pointer' : 'default' }}
                      onClick={() => !isEditingModel && handleCellClick(i, 'model')}
                    >
                      {isEditingModel ? (
                        <input
                          autoFocus
                          defaultValue={model}
                          onBlur={(e) => handleCellBlur(i, 'model', e.target.value)}
                          onKeyDown={handleCellKeyDown}
                          style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #1976d2', borderRadius: 3, padding: '2px 4px' }}
                        />
                      ) : (
                        <span style={editedCellStyle(i, 'model')}>
                          {model || ''}
                          {isEdited(i, 'model') && <span style={{ fontSize: 9, color: '#ff9800', marginLeft: 4 }}>edited</span>}
                        </span>
                      )}
                    </TableCell>
                  )}
                  <TableCell
                    style={{ cursor: onRowEdit ? 'pointer' : 'default' }}
                    onClick={() => !isEditingUnit && handleCellClick(i, 'unit')}
                  >
                    {isEditingUnit ? (
                      <select
                        autoFocus
                        defaultValue={unit || ''}
                        onChange={(e) => { handleCellBlur(i, 'unit', e.target.value); }}
                        onBlur={(e) => handleCellBlur(i, 'unit', e.target.value)}
                        onKeyDown={handleCellKeyDown}
                        style={{ width: 80, fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #1976d2', borderRadius: 3, padding: '2px 4px', background: '#fff' }}
                      >
                        {unit && !STANDARD_UNITS.includes(unit.toUpperCase()) && (
                          <option value={unit}>{unit} (current)</option>
                        )}
                        {!unit && <option value="">— select —</option>}
                        {STANDARD_UNITS.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={editedCellStyle(i, 'unit')}>
                        {unit || ''}
                        {isEdited(i, 'unit') && <span style={{ fontSize: 9, color: '#ff9800', marginLeft: 4 }}>edited</span>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    style={{ cursor: onRowEdit ? 'pointer' : 'default' }}
                    onClick={() => !isEditingQty && handleCellClick(i, 'quantity')}
                  >
                    {isEditingQty ? (
                      <input
                        autoFocus
                        type="number"
                        defaultValue={quantity != null ? quantity : ''}
                        onBlur={(e) => handleCellBlur(i, 'quantity', e.target.value ? Number(e.target.value) : null)}
                        onKeyDown={handleCellKeyDown}
                        style={{ width: 70, fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #1976d2', borderRadius: 3, padding: '2px 4px', textAlign: 'right' }}
                      />
                    ) : (
                      <span style={editedCellStyle(i, 'quantity')}>
                        {quantity != null ? quantity : ''}
                        {isEdited(i, 'quantity') && <span style={{ fontSize: 9, color: '#ff9800', marginLeft: 4 }}>edited</span>}
                      </span>
                    )}
                  </TableCell>
                  {showNotesColumn && (
                    <TableCell style={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {notes || ''}
                    </TableCell>
                  )}
                  <TableCell
                    style={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word', cursor: onRowEdit ? 'pointer' : 'default' }}
                    onClick={() => !isEditingReviewerNotes && handleCellClick(i, 'reviewerNotes')}
                  >
                    {isEditingReviewerNotes ? (
                      <textarea
                        autoFocus
                        defaultValue={reviewerNotes}
                        onBlur={(e) => handleCellBlur(i, 'reviewerNotes', e.target.value)}
                        onKeyDown={handleCellKeyDown}
                        rows={3}
                        style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #1976d2', borderRadius: 3, padding: '4px 6px', resize: 'vertical' }}
                      />
                    ) : (
                      <span style={editedCellStyle(i, 'reviewerNotes')}>
                        {reviewerNotes || ''}
                        {isEdited(i, 'reviewerNotes') && <span style={{ fontSize: 9, color: '#ff9800', marginLeft: 4 }}>edited</span>}
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    style={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', cursor: onRowEdit ? 'pointer' : 'default' }}
                    onClick={() => !isEditingSection && handleCellClick(i, 'section')}
                  >
                    {isEditingSection ? (
                      <input
                        autoFocus
                        defaultValue={section}
                        list={sectionListId}
                        onBlur={(e) => handleCellBlur(i, 'section', e.target.value)}
                        onKeyDown={handleCellKeyDown}
                        style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #1976d2', borderRadius: 3, padding: '2px 4px' }}
                      />
                    ) : (
                      <span style={editedCellStyle(i, 'section')}>
                        {section || ''}
                        {isEdited(i, 'section') && <span style={{ fontSize: 9, color: '#ff9800', marginLeft: 4 }}>edited</span>}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {addedRows.map((addedRow, ai) => (
              <TableRow key={`added-${ai}`} style={{ backgroundColor: '#e8f5e9' }}>
                <TableCell padding="checkbox">
                  <Tooltip title="Remove row">
                    <IconButton size="small" onClick={() => onRemoveAddedRow && onRemoveAddedRow(ai)}>
                      <DeleteIcon fontSize="small" style={{ color: '#c62828', fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ color: '#999', fontSize: 11 }}>+{ai + 1}</TableCell>
                <TableCell>
                  <textarea
                    value={addedRow.description || ''}
                    onChange={(e) => onEditAddedRow && onEditAddedRow(ai, 'description', e.target.value)}
                    placeholder="Description"
                    rows={2}
                    style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #a5d6a7', borderRadius: 3, padding: '4px 6px', resize: 'vertical', background: '#fff' }}
                  />
                </TableCell>
                {showSizeColumn && <TableCell />}
                {showManufacturerColumn && (
                  <TableCell>
                    <input
                      value={addedRow.manufacturer || ''}
                      onChange={(e) => onEditAddedRow && onEditAddedRow(ai, 'manufacturer', e.target.value)}
                      placeholder="Manufacturer"
                      style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #a5d6a7', borderRadius: 3, padding: '2px 4px', background: '#fff' }}
                    />
                  </TableCell>
                )}
                {showModelColumn && (
                  <TableCell>
                    <input
                      value={addedRow.model || ''}
                      onChange={(e) => onEditAddedRow && onEditAddedRow(ai, 'model', e.target.value)}
                      placeholder="Model"
                      style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #a5d6a7', borderRadius: 3, padding: '2px 4px', background: '#fff' }}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <select
                    value={addedRow.unit || ''}
                    onChange={(e) => onEditAddedRow && onEditAddedRow(ai, 'unit', e.target.value)}
                    style={{ width: 80, fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #a5d6a7', borderRadius: 3, padding: '2px 4px', background: '#fff' }}
                  >
                    <option value="">—</option>
                    {STANDARD_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </TableCell>
                <TableCell align="right">
                  <input
                    type="number"
                    value={addedRow.quantity != null ? addedRow.quantity : ''}
                    onChange={(e) => onEditAddedRow && onEditAddedRow(ai, 'quantity', e.target.value ? Number(e.target.value) : null)}
                    style={{ width: 70, fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #a5d6a7', borderRadius: 3, padding: '2px 4px', textAlign: 'right', background: '#fff' }}
                  />
                </TableCell>
                {showNotesColumn && <TableCell />}
                <TableCell>
                  <textarea
                    value={addedRow.reviewerNotes || ''}
                    onChange={(e) => onEditAddedRow && onEditAddedRow(ai, 'reviewerNotes', e.target.value)}
                    placeholder="Notes"
                    rows={2}
                    style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #a5d6a7', borderRadius: 3, padding: '4px 6px', resize: 'vertical', background: '#fff' }}
                  />
                </TableCell>
                <TableCell>
                  <input
                    value={addedRow.section || ''}
                    onChange={(e) => onEditAddedRow && onEditAddedRow(ai, 'section', e.target.value)}
                    list={sectionListId}
                    placeholder="Section"
                    style={{ width: '100%', fontSize: 'inherit', fontFamily: 'inherit', border: '1px solid #a5d6a7', borderRadius: 3, padding: '2px 4px', background: '#fff' }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default ImportTableCard
